import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  CheckCircle2, 
  MessageSquare, 
  Calendar, 
  ChevronRight, 
  Globe, 
  Clock, 
  AlertCircle,
  Loader2,
  ChevronLeft,
  Share2,
  ThumbsUp,
  XCircle,
  Eye,
  Send,
  Layers
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import axios from 'axios';
import { ContentIdea, BrandProfile } from '../types';

// Parse strings without timezone offset shifting
const parseLocalDate = (dateStr: string) => {
  if (!dateStr) return new Date();
  const parts = dateStr.substring(0, 10).split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // 0-based
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }
  return new Date(dateStr);
};

interface SharedData {
  shareInfo: {
    type: 'plan' | 'post';
    clientEmail?: string;
    message?: string;
    expiresAt: string;
  };
  brand: {
    id: string;
    companyName: string;
    logoUrl?: string;
    brandingDetails?: string;
  };
  plan?: {
    id: string;
    month: string;
    year: number;
  };
  content: ContentIdea[] | ContentIdea;
  previousFeedback?: any[];
}

import { doc, getDoc, collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { db } from '../firebase';

export const ShareView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<SharedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIdea, setSelectedIdea] = useState<ContentIdea | null>(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [reviewerName, setReviewerName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`/api/share/view/${token}`);
        const fullData = response.data;

        setData(fullData);
        
        const ideas = Array.isArray(fullData.content) ? fullData.content : [fullData.content];
        if (ideas.length > 0) {
          setSelectedIdea(ideas[0]);
        }
      } catch (err: any) {
        console.error(err);
        setError(err.response?.data?.error || err.message || "Failed to load shared content");
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchData();
  }, [token]);

  const handleSelectIdea = (idea: ContentIdea) => {
    setSelectedIdea(idea);
    setActiveSlideIndex(0);
  };

  const handleSubmitFeedback = async (ideaId: string, status?: 'approved' | 'rejected') => {
    if (!feedback.trim() && !status) return;
    
    setSubmitting(true);
    try {
      const newFeedback = {
        shareToken: token,
        brandId: data?.brand.id,
        planId: data?.plan?.id,
        ideaId: ideaId,
        reviewerName: reviewerName || "Client",
        status: status || 'comment',
        feedback: feedback || (status === 'approved' ? "Approved post" : "Requested revisions"),
      };

      const res = await axios.post('/api/share/feedback/create', newFeedback);
      
      setSuccessMsg("Feedback submitted successfully!");
      setFeedback('');
      
      // Update local state
      if (data) {
          const feedbackWithId = { ...newFeedback, createdAt: new Date().toISOString(), id: res.data.id };
          
          const updatedContent = Array.isArray(data.content) 
              ? data.content.map(i => i.id === ideaId ? { ...i, status: status || i.status } : i)
              : (data.content as any).id === ideaId ? { ...data.content, status: status || (data.content as any).status } : data.content;

          setData({
              ...data,
              previousFeedback: [feedbackWithId, ...(data.previousFeedback || [])],
              content: updatedContent
          });

          if (selectedIdea && selectedIdea.id === ideaId) {
             setSelectedIdea({ ...selectedIdea, status: status || selectedIdea.status });
          }
      }
      
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err) {
      console.error(err);
      alert("Failed to submit feedback. Check your internet connection.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading content for review...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-red-100">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/" className="inline-flex items-center text-indigo-600 font-semibold hover:underline">
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to Home
          </Link>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const expirationDate = new Date(data.shareInfo.expiresAt);
  const ideas = Array.isArray(data.content) ? data.content : [data.content];

  const currentIdeaIndex = ideas.findIndex(i => i.id === selectedIdea?.id);
  const prevIdea = currentIdeaIndex > 0 ? ideas[currentIdeaIndex - 1] : null;
  const nextIdea = currentIdeaIndex < ideas.length - 1 ? ideas[currentIdeaIndex + 1] : null;

    const renderDetailView = () => (
    <AnimatePresence mode="wait">
              {selectedIdea ? (
                <motion.div
                  key={selectedIdea.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
                >
                  <div className="p-6 md:p-8 flex flex-col md:flex-row gap-8">
                    {/* Visual Preview */}
                    <div className="w-full md:w-1/2 space-y-4">
                      <div className={`bg-gray-900 rounded-2xl overflow-hidden relative shadow-2xl ${
                        selectedIdea.format === 'story' ? 'aspect-[9/16] max-h-[70vh] w-auto mx-auto' : 'aspect-square'
                      }`}>
                         {/* Format Badge Overlay */}
                         <div className="absolute top-4 left-4 z-20 flex items-center gap-1.5 px-2.5 py-1 bg-black/40 backdrop-blur-md rounded-lg border border-white/10">
                           <div className={`w-1.5 h-1.5 rounded-full ${selectedIdea.format === 'feed' ? 'bg-indigo-400' : selectedIdea.format === 'story' ? 'bg-pink-400' : 'bg-orange-400'}`} />
                           <span className="text-[10px] font-black uppercase text-white tracking-widest font-mono">
                             {selectedIdea.format}
                           </span>
                         </div>

                         {selectedIdea.slides?.[activeSlideIndex]?.url ? (
                           selectedIdea.slides[activeSlideIndex].isVideo ? (
                             <video 
                               key={selectedIdea.slides[activeSlideIndex].url}
                               src={selectedIdea.slides[activeSlideIndex].url} 
                               controls 
                               className="w-full h-full object-contain"
                               autoPlay
                             />
                           ) : (
                             <img 
                               key={selectedIdea.slides[activeSlideIndex].url}
                               src={selectedIdea.slides[activeSlideIndex].url} 
                               alt={selectedIdea.concept} 
                               className="w-full h-full object-contain"
                             />
                           )
                         ) : (
                           <div className="w-full h-full flex items-center justify-center text-gray-500">
                               No preview available
                           </div>
                         )}

                         {selectedIdea.slides.length > 1 && (
                            <>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveSlideIndex(prev => (prev > 0 ? prev - 1 : selectedIdea.slides.length - 1));
                                }}
                                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center backdrop-blur-sm transition-all"
                              >
                                <ChevronLeft className="w-6 h-6" />
                              </button>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setActiveSlideIndex(prev => (prev < selectedIdea.slides.length - 1 ? prev + 1 : 0));
                                }}
                                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center backdrop-blur-sm transition-all"
                              >
                                <ChevronRight className="w-6 h-6" />
                              </button>
                              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur-sm px-3 py-1 rounded-full text-[10px] font-bold text-white uppercase tracking-widest">
                                {activeSlideIndex + 1} / {selectedIdea.slides.length}
                              </div>
                            </>
                         )}
                      </div>
                      <div className="flex gap-2 overflow-x-auto py-2 pr-2 custom-scrollbar">
                        {selectedIdea.slides.map((slide, idx) => (
                           <button 
                             key={slide.id} 
                             onClick={() => setActiveSlideIndex(idx)}
                             className={`w-16 h-16 rounded-lg border-2 flex-shrink-0 cursor-pointer overflow-hidden transition-all ${idx === activeSlideIndex ? 'border-indigo-600 ring-2 ring-indigo-600/20' : 'border-transparent opacity-60 hover:opacity-100'}`}
                           >
                             <img 
                               src={slide.thumbnailUrl || slide.url} 
                               className="w-full h-full object-cover" 
                               onError={(e) => {
                                 e.currentTarget.style.display = 'none';
                                 if (e.currentTarget.parentElement) {
                                   const div = document.createElement('div');
                                   div.className = 'w-full h-full flex items-center justify-center text-gray-400 bg-gray-100';
                                   div.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-image-off"><line x1="2" x2="22" y1="2" y2="22"/><path d="M10.41 10.41a2 2 0 1 1-2.83-2.83"/><line x1="13.5" x2="6" y1="13.5" y2="21"/><line x1="18" x2="21" y1="12" y2="15"/><path d="M3.59 3.59A1.99 1.99 0 0 0 3 5v14a2 2 0 0 0 2 2h14c.55 0 1.05-.22 1.41-.59"/><path d="M21 15V5a2 2 0 0 0-2-2H9"/></svg>';
                                   e.currentTarget.parentElement.appendChild(div);
                                 }
                               }}
                             />
                           </button>
                        ))}
                      </div>
                    </div>

                    {/* Content & Action */}
                    <div className="w-full md:w-1/2 flex flex-col">
                      <div className="mb-6">
                        {selectedIdea.date && (
                          <div className="flex items-center gap-2 mb-4">
                            <span className="flex items-center gap-1.5 text-xs font-bold uppercase text-indigo-600 bg-indigo-50 px-2.5 py-1 rounded-lg">
                              <Calendar className="w-3.5 h-3.5" />
                              Scheduled for {format(parseLocalDate(selectedIdea.date), 'EEEE, MMMM d, yyyy')}
                            </span>
                          </div>
                        )}
                        <div className="text-[13px] md:text-sm font-normal text-gray-800 leading-relaxed mb-4 whitespace-pre-wrap">
                          {selectedIdea.copy}
                        </div>
                        
                        <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                           <div className="flex items-center gap-1.5 mb-2">
                             <Layers className="w-3 h-3 text-gray-400" />
                             <span className="text-[9px] font-black uppercase text-gray-400 tracking-widest">Visual Concept</span>
                           </div>
                           <p className="text-sm text-gray-600 leading-relaxed italic">
                             "{selectedIdea.concept}"
                           </p>
                        </div>
                      </div>

                      {/* Approval Section */}
                      <div className="mt-auto space-y-4 pt-6 border-t border-gray-100">
                        {selectedIdea.status === 'approved' ? (
                          <div className="bg-green-50 border border-green-100 rounded-xl p-6 text-center">
                            <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto mb-3" />
                            <p className="text-green-800 font-black text-lg">Post Approved!</p>
                            <p className="text-sm text-green-600 mb-4">This content has been approved for publishing.</p>
                            <button
                              onClick={() => {
                                if (confirm("Undo approval for this post?")) {
                                   handleSubmitFeedback(selectedIdea.id, 'none' as any);
                                }
                              }}
                              className="text-xs font-bold text-gray-400 hover:text-gray-600 uppercase tracking-widest border-b border-gray-200 hover:border-gray-400 pb-0.5 transition-all"
                            >
                              Undo Approval
                            </button>
                          </div>
                        ) : (
                          <>
                            <div>
                               <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Your Name</label>
                               <input 
                                 type="text" 
                                 value={reviewerName}
                                 onChange={(e) => setReviewerName(e.target.value)}
                                 placeholder="e.g. John Smith"
                                 className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                               />
                            </div>
                            <div>
                               <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Feedback / Revisions</label>
                               <textarea 
                                 value={feedback}
                                 onChange={(e) => setFeedback(e.target.value)}
                                 placeholder="Tell us what you think or suggest changes..."
                                 className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 min-h-[100px] resize-none text-gray-900"
                               />
                            </div>
                            <div className="flex gap-3">
                               <button
                                 disabled={submitting}
                                 onClick={() => handleSubmitFeedback(selectedIdea.id, 'rejected')}
                                 className="flex-1 px-4 py-3 border border-gray-200 text-gray-600 rounded-xl font-bold hover:bg-gray-50 flex items-center justify-center gap-2"
                               >
                                 <MessageSquare className="w-4 h-4" /> Suggest Edits
                               </button>
                               <button
                                 disabled={submitting}
                                 onClick={() => handleSubmitFeedback(selectedIdea.id, 'approved')}
                                 className="flex-1 px-4 py-3 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-500 shadow-lg shadow-indigo-600/20 flex items-center justify-center gap-2"
                               >
                                 <ThumbsUp className="w-4 h-4" /> Approve
                               </button>
                            </div>
                          </>
                        )}
                        {successMsg && (
                          <motion.p 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-center text-sm font-bold text-green-600"
                          >
                            {successMsg}
                          </motion.p>
                        )}
                      </div>

                      {/* Post Navigation */}
                      <div className="flex items-center justify-between mt-auto pt-6 border-t border-gray-100">
                         {prevIdea ? (
                           <button 
                             onClick={() => handleSelectIdea(prevIdea)}
                             className="flex items-center gap-2 text-xs font-bold text-gray-500 hover:text-indigo-600 transition-colors group"
                            >
                             <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                             Previous Post
                           </button>
                         ) : <div></div>}

                         {nextIdea ? (
                           <button 
                             onClick={() => handleSelectIdea(nextIdea)}
                             className="flex items-center gap-2 text-xs font-bold text-gray-500 hover:text-indigo-600 transition-colors group"
                           >
                             Next Post
                             <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                           </button>
                         ) : <div></div>}
                      </div>

                      {/* Feedback History Feed */}
                      <div className="mt-8 pt-8 border-t border-gray-100">
                         <div className="flex items-center justify-between mb-4">
                            <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest">Feedback History</h4>
                            <span className="text-[10px] bg-gray-100 px-2 py-0.5 rounded-full text-gray-500">
                               {data.previousFeedback?.filter(f => String(f.ideaId) === String(selectedIdea.id)).length || 0} Comments
                            </span>
                         </div>
                         
                         <div className="space-y-4 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                            {data.previousFeedback?.filter(f => String(f.ideaId) === String(selectedIdea.id)).length === 0 ? (
                               <p className="text-xs text-center py-8 text-gray-400 font-medium">No previous feedback for this post yet.</p>
                            ) : (
                               data.previousFeedback?.filter(f => String(f.ideaId) === String(selectedIdea.id)).map((f) => (
                                 <div key={f.id} className="bg-gray-50 rounded-xl p-4 border border-gray-100 italic relative">
                                    <div className="flex items-center justify-between mb-2">
                                       <span className="text-[10px] font-black text-indigo-900 uppercase">{f.reviewerName}</span>
                                       <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded-full ${
                                         f.status === 'approved' ? 'bg-green-100 text-green-700 border border-green-200' :
                                         f.status === 'rejected' ? 'bg-red-100 text-red-700 border border-red-200' :
                                         'bg-blue-100 text-blue-700 border border-blue-200'
                                       }`}>
                                         {f.status || 'Comment'}
                                       </span>
                                    </div>
                                    <p className="text-xs text-gray-600 leading-relaxed leading-relaxed leading-relaxed">
                                       "{f.feedback || "No additional comments"}"
                                    </p>
                                    <div className="mt-2 text-[8px] text-gray-400 flex items-center gap-1">
                                       <Clock className="w-2 h-2" />
                                       {new Date(f.createdAt).toLocaleString()}
                                    </div>
                                 </div>
                               ))
                            )}
                         </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ) : (
                <div className="h-full flex items-center justify-center text-center p-12">
                   <div>
                     <Eye className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                     <h3 className="text-xl font-bold text-gray-400">Select a post to review</h3>
                   </div>
                </div>
              )}
            </AnimatePresence>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {data.brand.logoUrl ? (
              <img src={data.brand.logoUrl} alt={data.brand.companyName} className="h-10 w-10 rounded-lg object-contain bg-gray-50 p-1" />
            ) : (
              <div className="h-10 w-10 bg-indigo-100 rounded-lg flex items-center justify-center text-indigo-700 font-bold">
                {data.brand.companyName.charAt(0)}
              </div>
            )}
            <div>
              <h1 className="text-lg font-bold text-gray-900">{data.brand.companyName}</h1>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Expires {expirationDate.toLocaleDateString()}</span>
                {data.plan && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {data.plan.month} {data.plan.year}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="px-2 py-1 bg-green-50 text-green-700 text-[9px] md:text-xs font-black rounded-lg border border-green-100 uppercase tracking-widest whitespace-nowrap">
              Review Space
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {data.shareInfo.message && (
          <div className="mb-8 p-6 bg-indigo-50 border border-indigo-100 rounded-2xl relative overflow-hidden">
             <div className="absolute top-0 left-0 w-2 h-full bg-indigo-500 rounded-l-2xl"/>
             <h3 className="text-sm font-bold text-indigo-900 mb-2 flex items-center gap-2">
                <MessageSquare className="w-4 h-4" /> Message from {data.brand.companyName}
             </h3>
             <p className="text-gray-700 whitespace-pre-wrap leading-relaxed text-sm">"{data.shareInfo.message}"</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Left Column: List of Ideas */}
          <div className="lg:col-span-4 space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-gray-900">Content to Review</h2>
              <span className="text-xs bg-gray-200 px-2 py-1 rounded-full text-gray-700 font-medium">{ideas.length} Posts</span>
            </div>
            
            <div className="space-y-3 max-h-[calc(100vh-250px)] overflow-y-auto pr-2 custom-scrollbar">
              {ideas.map((idea) => (
                                <div key={idea.id} className="flex flex-col">
                  <button
                    onClick={() => {
                      if (isMobile && selectedIdea?.id === idea.id) {
                        setSelectedIdea(null);
                      } else {
                        handleSelectIdea(idea);
                        if (isMobile) {
                          setTimeout(() => {
                            document.getElementById(`idea-${idea.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          }, 100);
                        }
                      }
                    }}
                    id={`idea-${idea.id}`}
                    className={`w-full text-left p-4 rounded-xl transition-all border ${
                      selectedIdea?.id === idea.id 
                        ? 'bg-white border-indigo-500 shadow-md ring-1 ring-indigo-500' 
                        : 'bg-white border-gray-100 hover:border-gray-300 shadow-sm'
                    }`}
                  ><div className="flex items-start gap-3">
                     <div className="w-12 h-12 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
                      {idea.slides?.[0]?.thumbnailUrl ? (
                        <img 
                          src={idea.slides[0].thumbnailUrl} 
                          alt="" 
                          className="w-full h-full object-cover" 
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            if (e.currentTarget.parentElement) {
                               const div = document.createElement('div');
                               div.className = 'w-full h-full flex items-center justify-center text-gray-400 bg-gray-100';
                               div.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-image-off"><line x1="2" x2="22" y1="2" y2="22"/><path d="M10.41 10.41a2 2 0 1 1-2.83-2.83"/><line x1="13.5" x2="6" y1="13.5" y2="21"/><line x1="18" x2="21" y1="12" y2="15"/><path d="M3.59 3.59A1.99 1.99 0 0 0 3 5v14a2 2 0 0 0 2 2h14c.55 0 1.05-.22 1.41-.59"/><path d="M21 15V5a2 2 0 0 0-2-2H9"/></svg>';
                               e.currentTarget.parentElement.appendChild(div);
                            }
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                           <Eye className="w-5 h-5" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-bold uppercase text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
                          {idea.format}
                        </span>
                        {idea.date && (
                          <span className="text-[10px] font-bold uppercase text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {format(parseLocalDate(idea.date), 'MMM d')}
                          </span>
                        )}
                        {idea.status === 'approved' && <CheckCircle2 className="w-3 h-3 text-green-500" />}
                      </div>
                      <p className="text-sm font-semibold text-gray-900 truncate mb-1">{idea.concept}</p>
                      <p className="text-xs text-gray-500 line-clamp-1">{idea.copy}</p>
                    </div>
                  </div>
                </button>
                  
                  {isMobile && selectedIdea?.id === idea.id && (
                    <div className="mt-4 mb-6 lg:hidden animate-in slide-in-from-top-2 fade-in duration-200">
                      {renderDetailView()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

                    {/* Right Column: Detail & Feedback */}
          {!isMobile && (
            <div className="hidden lg:block lg:col-span-8">
              {renderDetailView()}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 text-center text-gray-400 text-sm">
        <p>Review space powered by <span className="font-bold text-indigo-600">PlataPost AI</span></p>
      </footer>
    </div>
  );
};
